import { Link, useLocation, useParams } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

/**
 * Breadcrumb labels for known campaign sub-routes.
 * Falls back to a title-cased URL segment for anything not listed.
 */
const ROUTE_LABELS = {
  chapters: "Story",
  characters: "Players",
  npcs: "NPCs",
  creatures: "Creatures",
  lore: "Lore",
  deities: "Deities",
  factions: "Factions",
  quests: "Quests",
  maps: "World Atlas",
  locations: "Locations",
  handouts: "Handouts",
  tables: "Tables",
  relationships: "Family Trees",
  "world-history": "World History",
  items: "Items",
  notes: "Personal Notes",
  party: "The Party",
  wiki: "Player Wiki",
  vtt: "Virtual Tabletop",
  "encounter-builder": "Encounters",
  combat: "Combat",
  "dm-screen": "DM Screen",
  oracle: "AI Oracle",
  // Singular aliases for entity-detail routes (/campaign/:id/:type/:id)
  item: "Items",
  creature: "Creatures",
  deity: "Deities",
  map: "World Atlas",
  location: "Locations",
  character: "Players",
};

const ROUTE_GROUP = {
  chapters: "Overview",
  characters: "Party",
  npcs: "Party",
  party: "Party",
  maps: "World",
  locations: "World",
  factions: "World",
  quests: "World",
  lore: "World",
  deities: "World",
  relationships: "World",
  "world-history": "World",
  items: "Vault",
  creatures: "Vault",
  tables: "Vault",
  handouts: "Vault",
  vtt: "Tools",
  "encounter-builder": "Tools",
  combat: "Tools",
  oracle: "Tools",
  "dm-screen": "Tools",
  wiki: "Tools",
  notes: "Tools",
  // Singular aliases for entity-detail routes
  item: "Vault",
  creature: "Vault",
  deity: "World",
  map: "World",
  location: "World",
  character: "Party",
};

const titleCase = (s = "") =>
  s.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

export const CampaignBreadcrumbs = ({ campaignName, extraCrumbs = [] }) => {
  const { campaignId } = useParams();
  const location = useLocation();
  const queryType = new URLSearchParams(location.search).get("type");

  // Split path: /campaign/:id/chapters/...  -> ["chapters", ...]
  const parts = location.pathname.replace(/^\/+|\/+$/g, "").split("/");
  const sectionIdx = parts.findIndex((p) => p === "campaign") + 2; // skip "campaign" + id
  const section = parts[sectionIdx];

  if (!section) return null;

  const sectionLabel = ROUTE_LABELS[section] || titleCase(section);
  const group = ROUTE_GROUP[section];

  // Companions override (characters?type=companion)
  let displaySectionLabel = sectionLabel;
  if (section === "characters" && queryType === "companion") displaySectionLabel = "Companions";
  if (section === "characters" && queryType === "summon") displaySectionLabel = "Summons";
  if (section === "characters" && queryType === "npc") displaySectionLabel = "NPCs";

  return (
    <nav
      className="flex items-center gap-1.5 text-xs text-muted-foreground py-3 px-1 border-b border-border/30 mb-4 flex-wrap"
      aria-label="Breadcrumb"
      data-testid="campaign-breadcrumbs"
    >
      <Link
        to="/dashboard"
        className="flex items-center gap-1 hover:text-primary transition-colors"
        data-testid="breadcrumb-link-home"
      >
        <Home className="w-3 h-3" />
        <span>Campaigns</span>
      </Link>
      <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
      <Link
        to={`/campaign/${campaignId}`}
        className="hover:text-primary transition-colors max-w-[180px] truncate"
        data-testid="breadcrumb-link-campaign"
      >
        {campaignName || "Campaign"}
      </Link>
      {group && (
        <>
          <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
          <span className="text-muted-foreground/80" data-testid="breadcrumb-link-group">{group}</span>
        </>
      )}
      <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
      <span className="text-foreground font-serif" data-testid="breadcrumb-link-section">
        {displaySectionLabel}
      </span>
      {extraCrumbs.map((c, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
          {c.to ? (
            <Link to={c.to} className="hover:text-primary transition-colors max-w-[200px] truncate" data-testid={`breadcrumb-link-extra-${i}`}>{c.label}</Link>
          ) : (
            <span className="text-foreground font-serif max-w-[260px] truncate" data-testid={`breadcrumb-link-extra-${i}`}>{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
};

export default CampaignBreadcrumbs;
